import DOMPurify from 'dompurify';

// React-Quill never leaves its editor truly empty: it keeps a '<p><br></p>'
// skeleton on mount, and a cleared editor ends up as '<p></p>'. Both look
// empty to the user, so both must be reported as an empty string — otherwise
// a `required` check passes on blank content and the form is dirty on open.
export const QUILL_INIT_CONTENT = '<p><br></p>';
export const QUILL_EMPTY_CONTENT = '<p></p>';

// What the API stores for a rich text image: the formats AttachmentService
// knows how to turn into a file, and the size beyond which it answers 400.
export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const normalizeRichTextValue = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  return value === QUILL_INIT_CONTENT || value === QUILL_EMPTY_CONTENT
    ? ''
    : value;
};

/** First image of a paste or drop, ignoring the other files it may carry. */
export const pickImageFile = (
  files: FileList | null | undefined,
): File | undefined =>
  files
    ? Array.from(files).find((file) => file.type.startsWith('image/'))
    : undefined;

export const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Stored rich text comes from people and API clients, so anything rendered as
// HTML goes through here: no scripts, event handlers or javascript: links.
// Formatting stays (classes, styles, images), and so does `target` on links,
// which Quill sets with rel="noopener noreferrer".
export const sanitizeRichText = (html?: string | null): string =>
  DOMPurify.sanitize(html ?? '', { ADD_ATTR: ['target'] });
